import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import {Button} from 'primereact/button';
import {
    InputSwitch,
    InputSwitchChangeEvent
} from 'primereact/inputswitch';
import {Panel} from 'primereact/panel';

import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';

import {__} from '~/shared/src/utilities/messageCatalog';
import Application from '~/shared/src/model/Application';

import {getTextColorForBackgroundColor} from '~/frontend/src/utilities/renderHelpers';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import logout from '~/frontend/src/actions/logout';
import {reload as messageCatalogReload} from '~/frontend/src/actions/messagecatalog';
import {reload as metaModelReload} from '~/frontend/src/actions/metamodel';
import {reload as configReload} from '~/frontend/src/actions/config';
import {reload as dataChangeEventsReload} from '~/frontend/src/actions/dataChangeEvents';
import userReload from '~/frontend/src/actions/user/reloadUser';
import userReloadPermissions from '~/frontend/src/actions/user/reloadPermissions';
import {
    hideDevTools,
    showDevTools
} from '~/frontend/src/actions/debugConfig';
import switchApplication from '~/frontend/src/actions/user/switchApplication';
import openPanelForItemType from '~/frontend/src/actions/panelManager/openPanelForItemType';


type Props = {

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;
};

type AllProps = Props;


const getColorScheme = () => matchMedia( '(prefers-color-scheme: dark)' ).matches ? 'dark' : 'light';


/**
 * user menu
 * @return {JSX.Element}
 * @constructor
 */
const UserButton = ( props : AllProps ) => {
    const {
              className
          }                                                                       = props,
          dispatch                                                                = useDispatch<AppDispatch>(),
          debugConfig                                                             = useSelector( ( state : StoreState ) => state.debugConfig ),
          user                                                                    = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          xhr                                                                     = useSelector( ( state : StoreState ) => state.xhr?.[ 'global_non_masking' ] ),
          userButton                                                              = useRef<ContentButtonType>( null ),
          groupMembershipsPanel                                                   = useRef<Panel>( null ),
          applicationsPanel                                                       = useRef<Panel>( null ),
          [ isGroupMembershipsPanelExpanded, setIsGroupMembershipsPanelExpanded ] = useState<boolean>( false ),
          [ isApplicationsPanelExpanded, setIsApplicationsPanelExpanded ]         = useState<boolean>( false ),
          [ colorScheme, setColorScheme ]                                         = useState<'dark' | 'light'>( getColorScheme() ),
          language                                                                = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          reloading                                                               = useMemo(
              () => {
                  let retVal = false;

                  if (
                      xhr?.[ 'userReloadPermission' ]
                      || xhr?.[ 'loadMetaModel' ]
                      || xhr?.[ 'reloadMetaModel' ]
                      || xhr?.[ 'loadMessageCatalog' ]
                      || xhr?.[ 'reloadMessageCatalog' ]
                      || xhr?.[ 'loadConfig' ]
                      || xhr?.[ 'reloadConfig' ]
                      || xhr?.[ 'loadUser' ]
                      || xhr?.[ 'reloadUser' ]
                  ) {
                      retVal = true;
                  }

                  return retVal;
              },
              [
                  xhr
              ]
          ),
          appSortFnc                                                              = useCallback(
              ( app1 : Application, app2 : Application ) => {
                  let label1 = app1.key.toLowerCase(),
                      label2 = app2.key.toLowerCase();

                  if ( app1.title ) {
                      label1 = app1.title.toLowerCase();
                  }
                  if ( app2.title ) {
                      label2 = app2.title.toLowerCase();
                  }

                  return label1 > label2 ? 1 : -1;
              },
              [
                  'onlyOnce'
              ]
          ),
          userMenu                                                                = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( user ) {
                      retVal = <Panel
                          className="p-0"
                          pt={{
                              content: {
                                  style: {
                                      border : 'none',
                                      padding: '0'
                                  }
                              }
                          }}
                      >
                          <label
                              className="yn-menu-title-label"
                          >
                              <span className="yn-secondary" style={{ fontSize: '1rem' }}>User-ID</span> {user.userId}
                          </label>
                          <Button
                              key="logout"
                              label={__( 'YN.base.common.logout' )}
                              icon="pi pi-power-off yn-delete"
                              className="yn-menu-button yn-no-rounded-borders"
                              style={{ width: '100%' }}
                              rounded={false}
                              text={true}
                              outlined={false}
                              onClick={( event ) => {
                                  userButton.current?.collapse( event );
                                  void dispatch( logout() );
                              }}
                          />
                          <Button
                              key="userSettings"
                              label={__( 'YN.base.common.userSettings' )}
                              icon="pi pi-user"
                              className="yn-menu-button yn-no-rounded-borders"
                              style={{ width: '100%' }}
                              rounded={false}
                              text={true}
                              outlined={false}
                              onClick={( event ) => {
                                  userButton.current?.collapse( event );
                                  dispatch( openPanelForItemType(
                                      'CFG',
                                      'User',
                                      {
                                          schema    : 'CFG',
                                          rootItemId: user.item?.id
                                      },
                                      undefined,
                                      event
                                  ) );
                              }}
                          />
                      </Panel>;
                  }

                  return retVal;
              },
              [
                  language,
                  user
              ]
          ),
          advancedSettingsMenu                                                    = useMemo(
              () => {
                  let retVal : React.ReactNode = null;

                  if ( user ) {
                      retVal = <Panel
                          className="p-0"
                          pt={{
                              content: {
                                  style: {
                                      border : 'none',
                                      padding: '0'
                                  }
                              }
                          }}
                      >
                          <label
                              key="advancedUserSettingsMenuTitle"
                              className="yn-menu-subtitle-label block mt-3"
                          >
                              {__( 'YN.base.common.advancedUserSettings' )}
                          </label>

                          <Panel
                              key="groupMemberships"
                              id="groupMemberships"

                              ref={groupMembershipsPanel}

                              header={
                                  <Button
                                      label={__( 'YN.base.common.advancedUserSettings.showGroups' )}
                                      icon="pi pi-users"
                                      rounded={false}
                                      text={true}
                                      className="yn-menu-button yn-no-rounded-borders"
                                      onClick={( event ) => {
                                          applicationsPanel.current?.collapse( event );
                                          groupMembershipsPanel.current?.toggle( event );
                                      }}
                                  >
                                      <span
                                          className="pi pi-chevron-right"
                                          style={{
                                              float                   : 'right',
                                              rotate                  : isGroupMembershipsPanelExpanded ? '90deg' : '0deg',
                                              transitionDuration      : '.2s',
                                              transitionProperty      : 'rotate',
                                              transitionTimingFunction: 'ease-in-out'
                                          }}
                                      />
                                  </Button>
                              }

                              onExpand={() => {
                                  setIsGroupMembershipsPanelExpanded( true );
                              }}
                              onCollapse={() => {
                                  setIsGroupMembershipsPanelExpanded( false );
                              }}

                              toggleable={true}
                              collapsed={true}
                              className="yn-confirm-option-panel"
                              pt={{
                                  root   : {
                                      style: {
                                          padding: '0'
                                      }
                                  },
                                  content: {
                                      className: 'yn-confirm-option-panel-content'
                                  }
                              }}
                          >
                              {user.groupMemberships.map( group => <label key={group}>{group}</label> )}
                          </Panel>

                          <Panel
                              key="applications"
                              id="applications"

                              ref={applicationsPanel}

                              header={
                                  <Button
                                      label={__( 'YN.base.common.advancedUserSettings.switchApplication' )}
                                      icon="fas fa-window-restore"
                                      rounded={false}
                                      text={true}
                                      className="yn-menu-button yn-no-rounded-borders"
                                      onClick={( event ) => {
                                          groupMembershipsPanel.current?.collapse( event );
                                          applicationsPanel.current?.toggle( event );
                                      }}
                                  >
                                      <span
                                          className="pi pi-chevron-right"
                                          style={{
                                              float                   : 'right',
                                              rotate                  : isApplicationsPanelExpanded ? '90deg' : '0deg',
                                              transitionDuration      : '.2s',
                                              transitionProperty      : 'rotate',
                                              transitionTimingFunction: 'ease-in-out'
                                          }}
                                      />
                                  </Button>
                              }

                              onExpand={() => {
                                  setIsApplicationsPanelExpanded( true );
                              }}
                              onCollapse={() => {
                                  setIsApplicationsPanelExpanded( false );
                              }}

                              toggleable={true}
                              collapsed={true}
                              className="yn-confirm-option-panel"
                              pt={{
                                  root   : {
                                      style: {
                                          padding: '0'
                                      }
                                  },
                                  content: {
                                      className: 'yn-confirm-option-panel-content'
                                  }
                              }}
                          >
                              {user.applications.sort( appSortFnc ).map( app => {
                                  const style : React.CSSProperties = {
                                      padding     : '.25rem .5rem',
                                      marginBottom: '1px',
                                      width       : '100%'
                                  };

                                  let label        = app.key,
                                      appClassName = 'yn-no-rounded-borders';

                                  if ( app.title ) {
                                      label = `${app.title} (${app.key})`;
                                  }

                                  if ( app.key === user.applicationName ) {
                                      appClassName += ' yn-bold';
                                  }

                                  if ( app.color ) {
                                      style.backgroundColor = `${app.color}`;
                                      style.color = `${getTextColorForBackgroundColor( app.color )}`;
                                  }

                                  return <Button
                                      key={app.key}
                                      label={label}
                                      icon={`yn-icon ${colorScheme === 'light' ? app.iconItemCssClass : app.darkIconItemCssClass}`}
                                      disabled={app.key === user.applicationName}
                                      className={appClassName}
                                      style={style}
                                      pt={{
                                          label: {
                                              className: 'yn-no-wrap'
                                          }
                                      }}
                                      rounded={false}
                                      text={true}
                                      outlined={false}
                                      onClick={( event ) => {
                                          userButton.current?.collapse( event );
                                          void dispatch( switchApplication( app.key ) );
                                      }}
                                  />;
                              } )}
                          </Panel>

                          <Button
                              key="reload"
                              label={__( 'YN.base.common.advancedUserSettings.reload' )}
                              icon="pi pi-refresh"
                              className="yn-menu-button yn-no-rounded-borders"
                              style={{ width: '100%' }}
                              rounded={false}
                              text={true}
                              outlined={false}
                              onClick={( event ) => {
                                  const asyncFnc = async () => {
                                      await dispatch( userReloadPermissions() );
                                      await dispatch( metaModelReload() );
                                      await dispatch( messageCatalogReload() );
                                      await dispatch( configReload() );
                                      await dispatch( dataChangeEventsReload() );
                                      await dispatch( userReload() );
                                  };

                                  userButton.current?.collapse( event );
                                  void asyncFnc();
                              }}
                          />
                      </Panel>;
                  }

                  return retVal;
              },
              [
                  colorScheme,
                  isApplicationsPanelExpanded,
                  isGroupMembershipsPanelExpanded,
                  language,
                  user
              ]
          ),
          developerMenu                                                           = useMemo(
              () => {
                  const retVal : React.ReactNode[] = [];

                  if ( user && config.sysGroups?.admin && user.isMemberOfGroup( config.sysGroups.admin ) && user.item ) {
                      retVal.push( <label
                          key="devMenuTitle"
                          className="yn-menu-subtitle-label block mt-3"
                      >
                          Dev functions
                      </label> );

                      retVal.push( <div
                          key="showDevTools"
                          className="yn-header-inputswitch"
                          style={{ padding: '0 1rem' }}
                      >
                          <InputSwitch
                              inputId="devMenuShowDevToolsButton"
                              checked={debugConfig?.show ?? false}
                              onChange={( event : InputSwitchChangeEvent ) => {
                                  const checked = event.value;

                                  if ( checked ) {
                                      dispatch( showDevTools() );
                                  }
                                  else {
                                      dispatch( hideDevTools() );
                                  }
                              }}
                          />
                          <label
                              htmlFor="devMenuShowDevToolsButton"
                          >
                              Show DevTools
                          </label>
                      </div> );
                  }

                  return retVal
              },
              [
                  debugConfig?.show ?? false,
                  language,
                  user
              ]
          );

    // ( on mount / unmount )
    // add/remove listener for color scheme switch
    useEffect(
        () => {
            const matchMediaChangeHandler = () => {
                setColorScheme( getColorScheme() );
            };

            matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', matchMediaChangeHandler );

            // unmount
            return () => {
                matchMedia( '(prefers-color-scheme: dark)' ).removeEventListener( 'change', matchMediaChangeHandler );
            }
        },
        []
    );

    return useMemo(
        () => {
            if ( user ) {
                return <ContentButton
                    id="page-menu-user-button"
                    ref={userButton}

                    horizontalContentAlign="left"
                    verticalContentAlign="bottom"
                    rounded={true}
                    text={true}
                    icon={!reloading ? 'fas fa-user-circle' : 'pi pi-spin pi-cog'}
                    className={`yn-usermenu-button ${className}`}
                    buttonClassName="yn-usermenu-button"
                    contentClassName="yn-contentbutton-overlay-content-page-menu"

                    onCollapse={( event, isOutsideClicked ) => {
                        if ( isOutsideClicked ) {
                            setIsGroupMembershipsPanelExpanded( false );
                            setIsApplicationsPanelExpanded( false );
                        }

                        return isOutsideClicked;
                    }}

                    content={
                        <Panel
                            style={{ width: '25rem' }}
                            pt={{
                                content: {
                                    style: {
                                        border : 'none',
                                        padding: '.5rem 1rem'
                                    }
                                }
                            }}
                        >
                            {userMenu}
                            {advancedSettingsMenu}
                            {developerMenu}
                        </Panel>
                    }
                />;

            }
            else {
                return <></>;
            }
        },
        [
            advancedSettingsMenu,
            className,
            developerMenu,
            language,
            reloading,
            user,
            userMenu
        ]
    );
}

export default UserButton;
